import { useDashboard } from '../contexts/DashboardContext'
import { useAuth } from '../contexts/AuthContext'
import { authApi } from '../services/api'
import { useState } from 'react'
import { formatDate } from '../utils/dateUtils'
import {
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  UserGroupIcon,
  KeyIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline'

export default function Dashboard() {
  const { stats, loading, error } = useDashboard()
  const { user } = useAuth()
  const [showVerificationBanner, setShowVerificationBanner] = useState(true)

  const [showOtpVerification, setShowOtpVerification] = useState(false)
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [otpLoading, setOtpLoading] = useState(false)
  const [otpError, setOtpError] = useState('')
  const [otpSuccess, setOtpSuccess] = useState('')

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
        {error}
      </div>
    )
  }

  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return
    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)
    setOtpError('')
    if (value && index < 5) {
      document.querySelector(`input[data-index="${index + 1}"]`)?.focus()
    }
    if (newOtp.every(d => d !== '') && value) {
      handleOtpVerification(newOtp.join(''))
    }
  }

  const handleOtpPaste = (e) => {
    e.preventDefault()
    const digits = e.clipboardData.getData('text').match(/\d/g)
    if (digits && digits.length >= 6) {
      const newOtp = digits.slice(0, 6)
      setOtp(newOtp)
      setOtpError('')
      document.querySelector(`input[data-index="5"]`)?.focus()
      handleOtpVerification(newOtp.join(''))
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.querySelector(`input[data-index="${index - 1}"]`)?.focus()
    }
  }

  const handleOtpVerification = async (code = otp.join('')) => {
    if (code.length !== 6) return
    setOtpLoading(true)
    setOtpError('')
    try {
      const response = await authApi.post('/auth/verify-otp-new', { code, email: user?.email })
      if (response.status === 200 && response.data.verified) {
        localStorage.setItem('user_data', JSON.stringify({ ...user, isVerified: true }))
        setOtpSuccess('Email verified successfully!')
        setShowVerificationBanner(false)
        setShowOtpVerification(false)
        setTimeout(() => window.location.reload(), 1500)
      } else {
        setOtp(['', '', '', '', '', ''])
        setOtpError(response.data.error || 'Invalid verification code')
      }
    } catch {
      setOtpError('Network error. Please try again.')
    } finally {
      setOtpLoading(false)
    }
  }

  const handleVerifyEmail = async () => {
    try {
      const response = await authApi.post('/auth/resend-otp', { email: user?.email })
      if (response.status === 200) {
        setShowOtpVerification(true)
        setOtpSuccess('Fresh verification code sent to your email!')
      } else {
        setOtpError('Failed to send verification code')
      }
    } catch {
      setOtpError('Network error. Please try again.')
    }
  }

  const usagePct = Math.round(stats?.subscription?.usage_percentage || 0)

  const metricCards = [
    { label: 'API Calls Used', value: stats?.subscription?.conversations_used || 0, icon: DocumentTextIcon, color: 'text-brand-600' },
    { label: 'Monthly Limit', value: stats?.subscription?.conversations_limit || 0, icon: ClockIcon, color: 'text-blue-600', sub: 'calls' },
    { label: 'Usage', value: `${usagePct}%`, icon: CheckCircleIcon, color: usagePct > 90 ? 'text-red-600' : usagePct > 70 ? 'text-amber-600' : 'text-brand-600', sub: 'of limit' },
    { label: 'Plan', value: (stats?.subscription?.plan || 'starter').toUpperCase(), icon: UserGroupIcon, color: 'text-violet-600', sub: stats?.subscription?.status || 'active' },
    { label: 'Conversations', value: stats?.stats?.messages_today || 0, icon: ChatBubbleLeftRightIcon, color: 'text-amber-600', sub: 'today' },
    { label: 'API Keys', value: stats?.stats?.total_api_keys || 0, icon: KeyIcon, color: 'text-slate-600', sub: 'active' },
  ]

  return (
    <div>
      {/* Email Verification Banner */}
      {user && !user.isVerified && showVerificationBanner && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="ml-3 flex-1">
              <p className="text-sm text-amber-800">
                <strong>Email Verification Required:</strong> Verify your email to access all features.
                A code has been sent to <strong>{user.email}</strong>.
              </p>
              <div className="mt-3 flex space-x-3">
                <button
                  onClick={handleVerifyEmail}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-amber-600 hover:bg-amber-700 transition-colors"
                >
                  <EnvelopeIcon className="h-4 w-4 mr-1.5" />
                  Verify Email
                </button>
                <button
                  onClick={() => setShowVerificationBanner(false)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification */}
      {showOtpVerification && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <h3 className="text-base font-semibold text-blue-900 mb-2">Email Verification</h3>
          <p className="text-sm text-blue-700 mb-4">
            Enter the 6-digit code sent to <strong>{user?.email}</strong>.
          </p>
          {otpSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-sm mb-4">{otpSuccess}</div>
          )}
          {otpError && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-sm mb-4">{otpError}</div>
          )}
          <div className="flex space-x-3 mb-4">
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                maxLength="1"
                data-index={index}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={index === 0 ? handleOtpPaste : null}
                className="w-11 h-11 text-center text-lg font-semibold border-2 border-slate-300 rounded-xl focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
                disabled={otpLoading}
              />
            ))}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => handleOtpVerification()}
              disabled={otpLoading || otp.some(d => d === '')}
              className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {otpLoading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button
              onClick={() => setShowOtpVerification(false)}
              className="px-4 py-2 text-sm font-medium rounded-lg text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">
          Welcome back, {user?.first_name || user?.fullName || 'Admin'}
        </h1>
        <p className="text-sm text-slate-500 mt-1">Here's your platform overview</p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {metricCards.map((card) => {
          const Icon = card.icon
          return (
            <div key={card.label} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{card.label}</span>
                <Icon className={`h-4 w-4 ${card.color}`} />
              </div>
              <div className={`text-2xl font-bold ${card.color}`}>{card.value}</div>
              {card.sub && <div className="text-[11px] text-slate-400 mt-0.5">{card.sub}</div>}
            </div>
          )
        })}
      </div>

      {/* Account & Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-5">Account Information</h3>
          <div className="space-y-4">
            {[
              { label: 'Email', value: stats?.user?.email, verified: stats?.user?.isVerified },
              { label: 'Plan', value: (stats?.subscription?.plan || 'starter').toUpperCase(), badge: true },
              { label: 'Account Type', value: (stats?.user?.userType || 'individual').replace('_', ' ') },
              { label: 'Status', value: stats?.subscription?.status || 'Active', statusBadge: true },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between">
                <span className="text-sm text-slate-500">{row.label}</span>
                {row.badge ? (
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    row.value === 'ENTERPRISE' ? 'bg-violet-50 text-violet-700'
                      : row.value === 'PROFESSIONAL' ? 'bg-blue-50 text-blue-700'
                      : 'bg-slate-100 text-slate-700'
                  }`}>{row.value}</span>
                ) : row.statusBadge ? (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">{row.value}</span>
                ) : (
                  <span className="text-sm text-slate-900 flex items-center">
                    {row.value}
                    {row.verified && <CheckCircleIcon className="h-4 w-4 text-green-500 ml-1.5" />}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-5">Usage Summary</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">API Calls This Month</span>
              <span className="text-sm font-medium text-slate-900">
                {stats?.subscription?.conversations_used || 0} / {stats?.subscription?.conversations_limit || 0}
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  usagePct > 90 ? 'bg-red-500' : usagePct > 70 ? 'bg-amber-500' : 'bg-brand-500'
                }`}
                style={{ width: `${Math.min(usagePct, 100)}%` }}
              />
            </div>
            {[
              { label: 'Messages Today', value: stats?.stats?.messages_today || 0 },
              { label: 'API Keys', value: `${stats?.stats?.total_api_keys || 0} active` },
              { label: 'Member Since', value: stats?.stats?.account_created ? formatDate(stats.stats.account_created) : 'Recently' },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between">
                <span className="text-sm text-slate-500">{row.label}</span>
                <span className="text-sm text-slate-900">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
